import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import "@mhmo91/passkey/client";
import { html, nothing, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { combineLatest, filter, from, merge, switchMap, takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import {
	passkeyEnrolled,
	passkeyOfferOpen,
	passkeyPendingOffer,
	passkeyPhase,
} from "./passkey.state.ts";
import { session } from "./session.state.ts";
import { signAction$, signResult$ } from "./sign.state.ts";

@schmancyElement("passkey-offer")
export class PasskeyOffer extends LocalizeMixin(SchmancyElement) {
	@property({ type: Boolean, attribute: "allow-new-user" }) allowNewUser =
		false;
	@property({ type: Boolean }) enabled = true;
	@state() private open = false;
	@state() private phase: "idle" | "working" | "done" | "error" = "idle";
	private pendingUid: string | undefined;
	private pendingIsNew = false;
	private enrolledReady = false;

	override connectedCallback() {
		super.connectedCallback();
		void passkeyEnrolled.ready.then(() => {
			this.enrolledReady = true;
			this.openPending();
		});
		from(passkeyPendingOffer.ready)
			.pipe(
				switchMap(() => combineLatest([session.$, passkeyPendingOffer.$])),
				takeUntil(this.disconnecting),
			)
			.subscribe(([current, pending]) => {
				if (!pending || current.status !== "member") return;
				passkeyPendingOffer.set(null);
				if (current.uid !== pending) return;
				this.pendingUid = pending;
				this.pendingIsNew = false;
				this.openPending();
			});
		merge(
			passkeyPhase.$,
			signResult$.pipe(
				filter(
					(result) =>
						(result.kind === "verifyOtp" || result.kind === "signOut") &&
						result.ok,
				),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe((result) => {
				if (typeof result === "string") {
					this.phase = result;
					return;
				}
				if (result.kind === "signOut") {
					passkeyPendingOffer.set(null);
					this.pendingUid = undefined;
					this.open = false;
					return;
				}
				if (result.kind === "verifyOtp") {
					this.pendingUid = result.uid;
					this.pendingIsNew = result.isNewUser === true;
					this.openPending();
				}
			});
	}

	override updated(changed: PropertyValues<this>) {
		passkeyOfferOpen.set(this.open);
		if (changed.has("enabled") && !this.enabled) this.open = false;
		if (changed.has("allowNewUser") || changed.has("enabled"))
			this.openPending();
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		passkeyOfferOpen.set(false);
	}

	private openPending() {
		if (
			!this.enrolledReady ||
			!this.enabled ||
			(this.pendingIsNew && !this.allowNewUser) ||
			!this.pendingUid ||
			passkeyEnrolled.value === this.pendingUid
		)
			return;
		this.pendingUid = undefined;
		passkeyPhase.set("idle");
		this.open = true;
	}

	private enroll() {
		if (this.phase === "working") return;
		signAction$.next({ kind: "enrollPasskey", requestId: crypto.randomUUID() });
	}

	override render() {
		return this.open
			? html`<passkey-enroll-prompt language=${this.uiLanguage} .phase=${this.phase}
      @passkey-enroll=${this.enroll}
      @passkey-enroll-done=${() => {
				this.open = false;
			}}
      @passkey-enroll-defer=${() => {
				this.open = false;
			}}></passkey-enroll-prompt>`
			: nothing;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"passkey-offer": PasskeyOffer;
	}
}
